import { HttpClient } from '../http-client/apiClient';
import { API_ENDPOINTS, AUTH_CRED } from '../http-client/httpConstants';
import {
  RegisterDto,
  LoginDto,
  CheckIdentifierRequestDto,
  TenantSelectDto,
  TwoFactorDto,
  RefreshTokenDto,
  LoginResponseDto,
  LogoutResponse,
  MeDto,
  ApiResponse,
  ForgetPasswordDto,
  ResetPasswordDto,
} from './types/auth.types';

export class AuthClient {
  static async register(data: RegisterDto): Promise<ApiResponse<LoginResponseDto>> {
    const response = await HttpClient.post<ApiResponse<LoginResponseDto>>(
      API_ENDPOINTS.AUTH.REGISTER,
      data,
    );
    if (response.data?.accessToken && response.data?.refreshToken) {
      localStorage.setItem(
        AUTH_CRED,
        JSON.stringify({
          token: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        }),
      );
    }
    return response;
  }

  static async login(data: LoginDto, tenantId?: string): Promise<ApiResponse<LoginResponseDto>> {
    const config = tenantId
      ? {
          headers: {
            'X-Tenant-ID': tenantId,
          },
        }
      : undefined;

    const response = await HttpClient.post<ApiResponse<LoginResponseDto>>(
      API_ENDPOINTS.AUTH.LOGIN,
      data,
      config,
    );
    if (response.data?.accessToken && response.data?.refreshToken) {
      localStorage.setItem(
        AUTH_CRED,
        JSON.stringify({
          token: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          tenantId: response.data.tenantId,
        }),
      );
    }
    return response;
  }

  /**
   * Step 1 of two-step login: Check if email/phone has associated tenants
   */
  static async checkIdentifier(
    data: CheckIdentifierRequestDto,
  ): Promise<ApiResponse<TenantSelectDto[]>> {
    return HttpClient.post<ApiResponse<TenantSelectDto[]>>(
      API_ENDPOINTS.AUTH.CHECK_IDENTIFIER,
      data,
    );
  }

  static async verifyTwoFactor(data: TwoFactorDto): Promise<ApiResponse<LoginResponseDto>> {
    const response = await HttpClient.post<ApiResponse<LoginResponseDto>>(
      API_ENDPOINTS.AUTH.VERIFY_2FA,
      data,
    );
    if (response.data?.accessToken && response.data?.refreshToken) {
      const credentials = HttpClient.getAuthCredentials();
      localStorage.setItem(
        AUTH_CRED,
        JSON.stringify({
          token: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          tenantId: response.data.tenantId || credentials?.tenantId,
        }),
      );
    }
    return response;
  }

  static async refresh(data: RefreshTokenDto): Promise<ApiResponse<LoginResponseDto>> {
    const response = await HttpClient.post<ApiResponse<LoginResponseDto>>(
      API_ENDPOINTS.AUTH.REFRESH,
      data,
    );
    if (response.data?.accessToken && response?.data?.refreshToken) {
      const credentials = HttpClient.getAuthCredentials();
      localStorage.setItem(
        AUTH_CRED,
        JSON.stringify({
          token: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          tenantId: response.data.tenantId || credentials?.tenantId,
        }),
      );
    }
    return response;
  }

  static async logout(): Promise<ApiResponse<LogoutResponse>> {
    const token = localStorage.getItem(AUTH_CRED);
    if (!token) {
      throw new Error('No refresh token found');
    }

    const parsed = JSON.parse(token) as { refreshToken?: string };
    if (!parsed.refreshToken) {
      throw new Error('Invalid token format');
    }
    const data = {
      refreshToken: parsed.refreshToken,
    } as RefreshTokenDto;
    const response = await HttpClient.post<ApiResponse<LogoutResponse>>(
      API_ENDPOINTS.AUTH.LOGOUT,
      data,
    );
    localStorage.removeItem(AUTH_CRED);
    return response;
  }

  static async getActiveSessions(userId: string): Promise<ApiResponse<number>> {
    return HttpClient.get<ApiResponse<number>>(`${API_ENDPOINTS.AUTH.SESSIONS}/${userId}`);
  }

  static async revokeAllSessions(userId: string): Promise<ApiResponse<boolean>> {
    return HttpClient.post<ApiResponse<boolean>>(`${API_ENDPOINTS.AUTH.REVOKE_SESSIONS}/${userId}`);
  }

  static async getCurrentUser(): Promise<ApiResponse<MeDto>> {
    return HttpClient.get<ApiResponse<MeDto>>(API_ENDPOINTS.AUTH.ME);
  }

  static async forgetPassword(data: ForgetPasswordDto): Promise<ApiResponse<null>> {
    return HttpClient.post<ApiResponse<null>>(API_ENDPOINTS.AUTH.FORGET_PASSWORD, data);
  }

  static async resetPassword(data: ResetPasswordDto): Promise<ApiResponse<null>> {
    return HttpClient.post<ApiResponse<null>>(API_ENDPOINTS.AUTH.RESET_PASSWORD, data);
  }

  //logout
  static async logoutUser(): Promise<ApiResponse<LogoutResponse>> {
    return HttpClient.post<ApiResponse<LogoutResponse>>(API_ENDPOINTS.AUTH.LOGOUT);
  }
}
